import { FilePen, FileUp, Globe2, MessageCircleQuestion, NotebookPen, SquarePlus } from "lucide-react";
import { useRef, type ReactNode } from "react";

import { useUpload } from "@/hooks/use-upload";
import { createMarkdownNote } from "@/lib/create-note";
import { NODE_COLOR } from "@/lib/palette";
import { uploadAccept } from "@/lib/upload-accept";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

function QuickItem({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      {icon}
      {label}
    </button>
  );
}

/** The quick-add ITEMS on their own, with no positioning or surface of their
 * own, so the same list can sit in the atlas's absolutely-placed popup and in a
 * viewport-aware popover on the left rail. Owning the hidden file input keeps
 * "Add file…" working wherever the list is rendered. */
export function QuickAddItems({
  onClose,
  onAddWebsite,
}: {
  onClose: () => void;
  onAddWebsite: () => void;
}) {
  const upload = useUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const act = (fn: () => void) => {
    onClose();
    fn();
  };
  return (
    <>
      <QuickItem
        icon={<FileUp className="h-3.5 w-3.5 text-primary/80" />}
        label="Add file…"
        onClick={() => act(() => fileRef.current?.click())}
      />
      <QuickItem
        icon={<Globe2 className="h-3.5 w-3.5 text-skyb/90" />}
        label="Add website…"
        onClick={() => act(onAddWebsite)}
      />
      <QuickItem
        icon={<NotebookPen className="h-3.5 w-3.5 text-primary/80" />}
        label="New handwritten note…"
        onClick={() => act(() => useShell.getState().requestCreate("note"))}
      />
      <QuickItem
        icon={<FilePen className="h-3.5 w-3.5 text-primary/80" />}
        label="New markdown note…"
        onClick={() => act(() => void createMarkdownNote())}
      />
      <QuickItem
        icon={<SquarePlus className="h-3.5 w-3.5" style={{ color: NODE_COLOR.org }} />}
        label="New org node…"
        onClick={() =>
          act(() => {
            useTabs.getState().activate("graph");
            useShell.getState().requestCreate("org");
          })
        }
      />
      <QuickItem
        icon={<MessageCircleQuestion className="h-3.5 w-3.5 text-muted-foreground" />}
        label="Ask the atlas"
        onClick={() => act(() => useTabs.getState().openAsk())}
      />
      {/* Hidden picker behind "Add file…". */}
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={uploadAccept()}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void upload(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}

/** The "add to the atlas" quick-add menu, opened by a two-click on empty space.
 * Shared so the ORBITS view offers the exact same affordance as the atlas:
 * files upload directly, markdown notes open straight in the editor, and
 * canvas/org creation routes through the shell create-intent (the atlas owns
 * those dialogs — a no-op tab switch when already there). */
export function QuickAddMenu({
  pos,
  onClose,
  onAddWebsite,
}: {
  pos: { x: number; y: number } | null;
  onClose: () => void;
  onAddWebsite: () => void;
}) {
  const upload = useUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const act = (fn: () => void) => {
    onClose();
    fn();
  };
  return (
    <>
      {pos && (
        <div
          className="absolute z-30 w-[176px] rounded-md border border-border bg-muted/95 p-1 shadow-deep backdrop-blur animate-rise-in"
          style={{ left: pos.x, top: pos.y }}
        >
          <p className="px-2 pb-1 pt-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Add to the atlas
          </p>
          <QuickItem
            icon={<FileUp className="h-3.5 w-3.5 text-primary/80" />}
            label="Add file…"
            onClick={() => act(() => fileRef.current?.click())}
          />
          <QuickItem
            icon={<Globe2 className="h-3.5 w-3.5 text-skyb/90" />}
            label="Add website…"
            onClick={() => act(onAddWebsite)}
          />
          <QuickItem
            icon={<NotebookPen className="h-3.5 w-3.5 text-primary/80" />}
            label="New handwritten note…"
            onClick={() => act(() => useShell.getState().requestCreate("note"))}
          />
          <QuickItem
            icon={<FilePen className="h-3.5 w-3.5 text-primary/80" />}
            label="New markdown note…"
            onClick={() => act(() => void createMarkdownNote())}
          />
          <QuickItem
            icon={<SquarePlus className="h-3.5 w-3.5" style={{ color: NODE_COLOR.org }} />}
            label="New org node…"
            onClick={() =>
              act(() => {
                useTabs.getState().activate("graph");
                useShell.getState().requestCreate("org");
              })
            }
          />
          <QuickItem
            icon={<MessageCircleQuestion className="h-3.5 w-3.5 text-muted-foreground" />}
            label="Ask the atlas"
            onClick={() => act(() => useTabs.getState().openAsk())}
          />
        </div>
      )}
      {/* Hidden picker behind "Add file…". */}
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={uploadAccept()}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void upload(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}
